import { Fab, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';

const WhatsAppIcon = ({ size = 24 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"
      fill="currentColor"
    />
  </svg>
);

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: theme.zIndex.speedDial,
  backgroundColor: '#25D366', // WhatsApp green
  color: 'white',
  width: 64,
  height: 64,
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px 0 rgba(37, 211, 102, 0.4)',
  '&:hover': {
    backgroundColor: '#1DA851', // Darker WhatsApp green
    transform: 'scale(1.1)',
    boxShadow: '0 6px 25px 0 rgba(37, 211, 102, 0.6)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  // Responsive positioning
  [theme.breakpoints.down('md')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    width: 56,
    height: 56,
  },
  // Pulse animation for attention
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 4px 20px 0 rgba(37, 211, 102, 0.4)',
    },
    '50%': {
      boxShadow: '0 6px 25px 0 rgba(37, 211, 102, 0.8)',
    },
    '100%': {
      boxShadow: '0 4px 20px 0 rgba(37, 211, 102, 0.4)',
    },
  },
}));

interface WhatsAppFabProps {
  phoneNumber?: string;
  message?: string;
}

export default function WhatsAppFab({
  phoneNumber = '+34 623 76 38 74',
  message = 'Hola, me gustaría obtener más información sobre InversorHub.',
}: WhatsAppFabProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleWhatsAppClick = () => {
    // Format phone number (remove spaces and special characters except +)
    const formattedPhone = phoneNumber.replace(/\s/g, '').replace(/[^\d+]/g, '');

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${formattedPhone.replace('+', '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip
      title="Contacta con nosotros por WhatsApp"
      placement="left"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        },
        arrow: {
          sx: {
            color: 'rgba(0, 0, 0, 0.8)',
          },
        },
      }}
    >
      <StyledFab
        onClick={handleWhatsAppClick}
        aria-label="Contactar por WhatsApp"
        size={isMobile ? 'medium' : 'large'}
      >
        <WhatsAppIcon size={isMobile ? 24 : 28} />
      </StyledFab>
    </Tooltip>
  );
}
